import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Search, Gift, Undo2, CheckCircle2, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { friendlyError } from "@/lib/errors";

/**
 * /admin/testers — Beta Testers management
 *
 * List every case in the platform. Admins can waive the preparation fee on
 * any case so the owner can use the full workflow without paying. Useful for
 * beta testers and comp'd family/friend accounts.
 */

const centsToUSD = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function AdminTesters() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showOnlyWaived, setShowOnlyWaived] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = showOnlyWaived ? { waived: true } : {};
      const { data } = await api.get("/admin/cases", { params });
      setRows(data);
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't load the cases list."));
    } finally {
      setLoading(false);
    }
  }, [showOnlyWaived]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.owner_email, r.owner_name, r.beneficiary_name, r.petitioner_name,
        r.id, r.stage,
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  const stats = useMemo(() => ({
    total: rows.length,
    waived: rows.filter((r) => r.fee_waived).length,
    paid: rows.filter((r) => r.payment_status === "paid" && !r.fee_waived).length,
  }), [rows]);

  const setCustomFee = async (row, cents, reason) => {
    setBusyId(row.id);
    try {
      const { data } = await api.post(`/admin/cases/${row.id}/fee-override`, {
        override_total_cents: cents,
        reason: reason || undefined,
      });
      setRows((cur) => cur.map((r) => (r.id === row.id
        ? { ...r, fee_override_cents: data.fee_override_cents, fee_override_reason: data.fee_override_reason }
        : r)));
      toast.success(
        cents === null
          ? "Custom fee cleared — this case now uses the standard fee schedule."
          : `Custom fee set: ${centsToUSD(cents)} for this case only.`,
      );
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't update the custom fee."));
    } finally {
      setBusyId(null);
    }
  };

  const toggleWaive = async (row) => {
    setBusyId(row.id);
    try {
      let reason = row.fee_waived_reason;
      if (!row.fee_waived) {
         
        reason = window.prompt(
          `Why are you waiving the fee for ${row.owner_email || row.owner_name}?`,
          "Beta tester — comp'd by admin",
        );
        if (reason === null) { setBusyId(null); return; } // user canceled
      }
      const { data } = await api.post(`/admin/cases/${row.id}/waive-fee`, {
        waive: !row.fee_waived,
        reason,
      });
      setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, ...data, fee_waived_reason: data.fee_waived_reason } : r)));
      toast.success(
        data.fee_waived
          ? `Fee waived — ${row.owner_email} can now use this case for free.`
          : `Fee waiver reverted — this case is now billable again.`,
      );
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't update the fee waiver."));
    } finally {
      setBusyId(null);
    }
  };

  if (user === null) return null;
  if (!user || user.role !== "admin") {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-6 py-14 text-center">
          <h1 className="font-display text-xl font-bold mb-2">Admin only</h1>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            Sign in with an administrator account to manage testers.
          </p>
          <Link to="/" className="btn-primary inline-block mt-4">Back to home</Link>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-6 py-10" data-testid="admin-testers-page">
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg" style={{ background: "var(--ai-bg)" }}>
              <Gift className="w-6 h-6" style={{ color: "var(--ai-text)" }} />
            </div>
            <div>
              <h1 className="font-display text-3xl font-extrabold">Beta Testers</h1>
              <p className="text-sm max-w-lg" style={{ color: "var(--text-secondary)" }}>
                Waive the preparation fee on specific cases so trusted testers
                can use the full workflow at no charge. All waivers are logged.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/pricing" className="btn-ghost text-sm">Pricing config</Link>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-5">
          <StatCard label="Total cases" value={stats.total} testId="stat-total" />
          <StatCard label="Fee waived" value={stats.waived} accent tone="ai" testId="stat-waived" />
          <StatCard label="Paid via Stripe" value={stats.paid} tone="ok" testId="stat-paid" />
        </div>

        <div className="card-surface p-4 mb-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-secondary)" }} />
            <input
              type="text"
              placeholder="Search by email, name, or beneficiary…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="field-input pl-9"
              data-testid="admin-testers-search"
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyWaived}
              onChange={(e) => setShowOnlyWaived(e.target.checked)}
              data-testid="admin-testers-waived-filter"
            />
            Show only waived
          </label>
        </div>

        <div className="card-surface overflow-hidden">
          {loading ? (
            <div className="p-8 flex items-center gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Loading cases…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm" style={{ color: "var(--text-secondary)" }}>
              No cases match your search.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--border-stone)" }} data-testid="admin-testers-list">
              {filtered.map((r) => (
                <CaseRow key={r.id} row={r} busy={busyId === r.id} onToggle={() => toggleWaive(r)}
                         onSetFee={(cents, reason) => setCustomFee(r, cents, reason)} />
              ))}
            </ul>
          )}
        </div>

        <p className="text-xs mt-4" style={{ color: "var(--text-secondary)" }}>
          Waiving a fee sets the case&apos;s payment status to <code>waived</code> — the client can proceed at no charge. Setting a <strong>custom fee</strong> replaces the standard fee schedule for that one case only (all other cases keep the global pricing). All changes are audit-logged.
        </p>
      </div>
    </AppShell>
  );
}

const STAT_TONES = {
  ai: { bg: "var(--ai-bg)", border: "var(--ai-border)", color: "var(--ai-text)" },
  ok: { bg: "var(--success-bg)", border: "#A7F3D0", color: "var(--success-text)" },
  default: { bg: "#FFFFFF", border: "var(--border-stone)", color: "var(--action)" },
};

function StatCard({ label, value, accent, tone, testId }) {
  const { bg, border, color } = STAT_TONES[tone] || STAT_TONES.default;
  return (
    <div
      className="card-surface p-4"
      style={{ background: bg, borderColor: border }}
      data-testid={testId}
    >
      <div className="text-[10.5px] uppercase tracking-wider mb-1" style={{ color: "var(--text-secondary)" }}>
        {label}
      </div>
      <div className="font-display font-bold text-3xl" style={{ color: accent ? color : "var(--text-primary)" }}>
        {value}
      </div>
    </div>
  );
}

function CaseRow({ row, busy, onToggle, onSetFee }) {
  const [expanded, setExpanded] = useState(false);
  const [feeInput, setFeeInput] = useState(
    row.fee_override_cents != null ? (row.fee_override_cents / 100).toFixed(2) : "",
  );
  const [feeReason, setFeeReason] = useState(row.fee_override_reason || "");
  const applyFee = () => {
    const dollars = parseFloat(feeInput);
    if (Number.isNaN(dollars) || dollars < 0) return;
    onSetFee(Math.round(dollars * 100), feeReason.trim());
  };
  return (
    <li className="p-4 flex flex-col gap-2" data-testid={`admin-testers-row-${row.id}`}>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-display font-bold text-sm truncate" style={{ color: "var(--text-primary)" }}>
              {row.owner_email || row.owner_name || row.id}
            </span>
            {row.owner_role === "admin" && (
              <span className="pill" style={{ background: "#F3F4F6", color: "#374151" }}>admin</span>
            )}
            {row.fee_waived && (
              <span className="pill pill-ai" data-testid="row-waived-badge">
                <Gift className="w-3 h-3" /> Fee waived
              </span>
            )}
            {row.payment_status === "paid" && !row.fee_waived && (
              <span className="pill pill-ok">
                <CheckCircle2 className="w-3 h-3" /> Paid {centsToUSD(row.paid_amount_cents)}
              </span>
            )}
          </div>
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
            {row.case_type} · {row.stage} · Beneficiary: {row.beneficiary_name || "—"}
            {row.children_count > 0 && ` · +${row.children_count} child${row.children_count === 1 ? "" : "ren"}`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="btn-ghost text-xs"
            data-testid={`row-expand-${row.id}`}
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          <button
            type="button"
            onClick={onToggle}
            disabled={busy}
            className={row.fee_waived ? "btn-ghost text-xs" : "btn-primary text-xs"}
            data-testid={`row-toggle-waive-${row.id}`}
          >
            {busy ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />
            ) : row.fee_waived ? (
              <Undo2 className="w-3.5 h-3.5 inline mr-1" />
            ) : (
              <Gift className="w-3.5 h-3.5 inline mr-1" />
            )}
            {row.fee_waived ? "Revert waiver" : "Waive fee"}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="text-xs mt-1 pt-2 border-t space-y-1" style={{ borderColor: "var(--border-stone)", color: "var(--text-secondary)" }}>
          <div><strong>Case ID:</strong> <code>{row.id}</code></div>
          {row.petitioner_name && <div><strong>Petitioner:</strong> {row.petitioner_name}</div>}
          <div><strong>Created:</strong> {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}</div>
          {row.fee_waived && (
            <div><strong>Waived reason:</strong> {row.fee_waived_reason || "—"}{" "}
              {row.fee_waived_by && <>({row.fee_waived_by})</>}
            </div>
          )}
          {!row.fee_waived && (
            <div className="flex items-end gap-2 flex-wrap pt-1" data-testid={`row-custom-fee-editor-${row.id}`}>
              <label className="flex flex-col gap-1">
                <span className="text-[10.5px] uppercase tracking-wider">Custom fee for this case (USD)</span>
                <input
                  type="number" min="0" step="0.01" placeholder="e.g. 195.00"
                  value={feeInput}
                  onChange={(e) => setFeeInput(e.target.value)}
                  className="field-input w-36"
                  data-testid={`row-custom-fee-input-${row.id}`}
                />
              </label>
              <label className="flex flex-col gap-1 flex-1 min-w-[180px]">
                <span className="text-[10.5px] uppercase tracking-wider">Reason (shown on the client&apos;s quote)</span>
                <input
                  type="text" placeholder="e.g. Hardship discount"
                  value={feeReason}
                  onChange={(e) => setFeeReason(e.target.value)}
                  className="field-input"
                  data-testid={`row-custom-fee-reason-${row.id}`}
                />
              </label>
              <button type="button" onClick={applyFee} disabled={busy || feeInput === ""}
                      className="btn-primary text-xs" data-testid={`row-custom-fee-apply-${row.id}`}>
                Apply custom fee
              </button>
              {row.fee_override_cents != null && (
                <button type="button" onClick={() => { setFeeInput(""); setFeeReason(""); onSetFee(null, ""); }}
                        disabled={busy} className="btn-ghost text-xs"
                        data-testid={`row-custom-fee-clear-${row.id}`}>
                  Clear (use standard fee)
                </button>
              )}
            </div>
          )}
          <Link to={`/case/${row.id}`} className="text-[var(--action)] font-semibold inline-block mt-1">
            Open case →
          </Link>
        </div>
      )}
    </li>
  );
}
